// Template picker (M14; M20: by style family, the Classic set hidden): every template, with live thumbnails in
// the restaurant's brand. A click makes the ad, filled with the photo "Make an ad" started from (or the active
// Photos photo).
import { Palette } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLang, useT } from '../../../i18n';
import { TEMPLATES } from '../../templates/catalog';
import { CATEGORIES, FAMILIES, FORMATS } from '../../templates/dsl';
import { instantiate } from '../../templates/instantiate';
import { renderArtboard } from '../../io/exportAd';
import { brandData, createAd, useAdUi } from '../../ad/actions';
import { loadBrand, useBrand } from '../../ad/brand';
import { useLibrary } from '../../../state/library';
import { useDesign } from '../../state';
import s from './Ad.module.css';

const thumbs = new Map<string, string>();

async function thumbFor(id: string, version: number): Promise<string> {
  const key = `${id}@${version}`;
  const hit = thumbs.get(key);
  if (hit) return hit;
  const t = TEMPLATES.find((x) => x.id === id)!;
  const doc = instantiate(t, await brandData(await loadBrand()));
  const f = FORMATS[t.format];
  const c = await renderArtboard(doc, 340 / Math.max(f.w, f.h));
  const url = URL.createObjectURL(await c.convertToBlob({ type: 'image/png' }));
  thumbs.set(key, url);
  return url;
}

export function TemplatePicker() {
  const t = useT();
  const lang = useLang((l) => l.lang);
  const brandVersion = useBrand((b) => b.version);
  const busy = useAdUi((a) => a.busy);
  const fromPhoto = useAdUi((a) => a.fromPhoto);
  const active = useLibrary((l) => l.activeId);
  const photo = useLibrary((l) => l.photos.find((p) => p.id === (fromPhoto ?? l.activeId)) ?? null);
  const hasAd = useDesign((d) => !!d.doc?.template);
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    void (async () => {
      for (const tp of TEMPLATES) {
        const u = await thumbFor(tp.id, brandVersion).catch(() => '');
        if (!alive) return;
        setUrls((m) => ({ ...m, [tp.id]: u }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [brandVersion]);

  return (
    <div className={s.picker} data-testid="template-picker">
      <div className={s.pickerHead}>
        <h2 style={{ margin: 0 }}>{t('adPickTemplate')}</h2>
        <button data-testid="open-brand" onClick={() => useAdUi.setState({ brandOpen: true })}>
          <Palette className="icon" aria-hidden="true" /> {t('adMyBrand')}
        </button>
        {hasAd && (
          <button data-testid="picker-close" onClick={() => useAdUi.setState({ picker: false })}>
            {t('adBackToAd')}
          </button>
        )}
        <span className={s.small}>
          {photo ? t('adWithPhoto', { name: photo.name }) : active === null ? t('adNoPhoto') : ''}
        </span>
        {busy && <span role="status">{t('adWorking')}</span>}
      </div>
      {FAMILIES.map((fam) => (
        <section key={fam.id} data-testid={`family-${fam.id}`}>
          <h3 style={{ margin: '14px 0 0' }}>{fam.name[lang]}</h3>
          <div className={s.small}>{fam.note[lang]}</div>
          <div className={s.grid}>
            {TEMPLATES.filter((tp) => tp.family === fam.id).map((tp) => (
              <button
                key={tp.id}
                className={s.card}
                data-testid={`template-${tp.id}`}
                disabled={!!busy}
                onClick={() => void createAd(tp.id, photo?.id ?? null)}
              >
                {urls[tp.id] ? (
                  <img className={s.thumb} src={urls[tp.id]} alt="" />
                ) : (
                  <div className={s.thumb} aria-hidden="true" />
                )}
                <strong>{tp.name[lang]}</strong>
                <span className={s.small}>
                  {CATEGORIES.find((c) => c.id === tp.category)?.name[lang]} · {FORMATS[tp.format].px}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
